import { Input } from '@shared/ui';
import { Form } from 'antd';
import { Ban } from 'lucide-react';
import React, { useState } from 'react';
import { useCreateBrandCancellationRequest } from '../lib/hooks/useCreateBrandCancellationRequest';
import { useCreateInfluencerCancellationRequest } from '../lib/hooks/useCreateInfluencerCancellationRequest';
import { ICampaignAlterationApprovalRequest } from '../lib/types';
import styles from './styles.module.scss';

interface CancelCreatorFormProps {
  creatorName: string;
  campaignInfluencerId: string;
  briefId?: string;
  onSuccess?: () => void;
  onCancel: () => void;
  viewOnly?: boolean;
  alterationRequest?: ICampaignAlterationApprovalRequest;
}

export const CancelCreatorForm: React.FC<CancelCreatorFormProps> = ({
  creatorName,
  campaignInfluencerId,
  briefId,
  onSuccess,
  onCancel,
  viewOnly = false,
  alterationRequest,
}) => {
  const [cancelType, setCancelType] = useState<'Brand' | 'Influencer'>(
    alterationRequest ? (alterationRequest.type === 'Brand-Cancellation' ? 'Brand' : 'Influencer') : 'Brand'
  );
  const [cancelReason, setCancelReason] = useState(
    alterationRequest ? alterationRequest.remarks : ''
  );

  const { createBrandCancellation, isCreating: isBrandCancelling } = useCreateBrandCancellationRequest(briefId, onSuccess);
  const { createInfluencerCancellation, isCreating: isInfluencerCancelling } = useCreateInfluencerCancellationRequest(briefId, onSuccess);

  const isCreating = isBrandCancelling || isInfluencerCancelling;
  const canSubmit = cancelReason.trim().length > 0;

  const handleConfirm = () => {
    if (viewOnly) return;
    if (!canSubmit) return;
    const remarks = cancelReason.trim();

    if (cancelType === 'Brand') {
      createBrandCancellation({
        campaignInfluencerId,
        type: 'Brand-Cancellation',
        remarks,
      });
    } else {
      createInfluencerCancellation({
        campaignInfluencerId,
        type: 'Influencer-Cancellation',
        remarks,
      });
    }
  };

  return (
    <Form layout="vertical" onFinish={handleConfirm} className={styles.formContainer}>
      <div className={styles.cancelWarning}>
        <Ban size={18} style={{ color: '#e11d48', flexShrink: 0, marginTop: '2px' }} />
        <p className={styles.cancelWarningText}>
          This will mark <strong>{creatorName}</strong> as cancelled in campaign execution. This action is intended for admin use only.
        </p>
      </div>

      {/* Cancellation Initiator Options */}
      <Form.Item label={<span className={styles.label}>Cancellation Initiated By</span>} style={{ marginBottom: 0 }}>
        <div className={styles.directionContainer}>
          <button
            type="button"
            className={`${styles.directionBtn} ${cancelType === 'Brand' ? styles.activeUp : ''}`}
            onClick={viewOnly ? undefined : () => setCancelType('Brand')}
            style={viewOnly ? { cursor: 'default' } : undefined}
          >
            Brand Cancelled
          </button>
          <button
            type="button"
            className={`${styles.directionBtn} ${cancelType === 'Influencer' ? styles.activeDown : ''}`}
            onClick={viewOnly ? undefined : () => setCancelType('Influencer')}
            style={viewOnly ? { cursor: 'default' } : undefined}
          >
            Creator Cancelled
          </button>
        </div>
      </Form.Item>

      <Form.Item label={<span className={styles.label}>Cancellation reason</span>} style={{ marginBottom: 0 }}>
        <Input.TextArea
          className={styles.textarea}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder={`Why is this creator being cancelled by the ${cancelType.toLowerCase()}?`}
          autoFocus={!viewOnly}
          rows={4}
          disabled={viewOnly}
        />
      </Form.Item>

      {!viewOnly && (
        <div className={styles.footer}>
          <button type="button" onClick={onCancel} className={styles.cancelBtn}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || isCreating}
            className={`${styles.confirmBtn} ${styles.dangerBtn}`}
          >
            Cancel Creator
          </button>
        </div>
      )}
    </Form>
  );
};
